import React, { useState } from "react";
import { Product, Review, GramOption } from "@/lib/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ReviewForm } from "./ReviewForm";
import { ReviewList } from "./ReviewList";
import { X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface ProductModalProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onAddToCart: (product: Product, quantity: number, selectedGramOption?: GramOption) => void;
  reviews: Review[];
  onAddReview: (review: { productId: string; rating: number; comment: string; userName: string; userId: string }) => void;
}

export const ProductModal = React.memo(function ProductModal({ product, open, onClose, onAddToCart, reviews, onAddReview }: ProductModalProps) {
  if (!product) return null;

  const [imgSrc, setImgSrc] = React.useState(product.image);
  const [quantity, setQuantity] = useState(1);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedGramOption, setSelectedGramOption] = useState<GramOption | null>(
    product.gramOptions && product.gramOptions.length > 0 ? product.gramOptions[0] : null
  );
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const isArchived = product.status === 'archived';
  const { currentUser } = useAuth();
  const descriptionRef = React.useRef<HTMLDivElement>(null);

  const handleAddToCart = () => {
    // For products with gram options, always use the selected gram option (or first one by default)
    const gramOptionToUse = selectedGramOption || (product.gramOptions && product.gramOptions.length > 0 ? product.gramOptions[0] : null);
    
    let productToAdd = product;
    if (gramOptionToUse) {
      productToAdd = {
        ...product,
        price: gramOptionToUse.price,
        title: `${product.title} (${gramOptionToUse.label || gramOptionToUse.weight + 'g'})`
      };
    }
    onAddToCart(productToAdd, quantity, gramOptionToUse || undefined);
    setQuantity(1);
  };

  // Calculate current price based on selected gram option or default price
  const getCurrentPrice = () => {
    if (product.gramOptions && product.gramOptions.length > 0) {
      return selectedGramOption ? selectedGramOption.price : product.gramOptions[0].price;
    }
    return product.price;
  };

  const getCurrentTotal = () => {
    const currentPrice = getCurrentPrice();
    return currentPrice + (currentPrice * (product.gst / 100)) + product.shippingFee;
  };

  const handleSubmitReview = (review: { name: string; rating: number; comment: string }) => {
    if (!currentUser) {
      alert("You must be logged in to submit a review.");
      return;
    }
    onAddReview({
      rating: review.rating,
      comment: review.comment,
      userName: review.name,
      productId: product.id,
      userId: currentUser.uid,
    });
    setShowReviewForm(false);
  };

  const handleToggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
    
    // Scroll to description smoothly when expanding
    if (!isDescriptionExpanded && descriptionRef.current) {
      setTimeout(() => {
        descriptionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'start'
        });
      }, 100);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="w-[95vw] max-w-4xl max-h-[85vh] p-0 animate-scale-in rounded-2xl shadow-2xl border-2 border-[#FF6B35]/30 bg-[#F9F7F1] overflow-hidden"
      >
        <DialogDescription className="sr-only">
          Product details and reviews for {product.title}
        </DialogDescription>
        <div className="relative h-full flex flex-col max-h-[85vh]">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-50 bg-white/90 hover:bg-white rounded-full p-2 transition-colors shadow-lg border border-gray-200 touch-manipulation"
          >
            <X className="h-5 w-5 text-[#2F2F2F]" />
          </button>
          <div className="flex-1 overflow-y-auto">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image Section */}
              <div className="relative h-64 md:h-96 w-full bg-[#F9F7F1] flex items-center justify-center p-6">
                <img
                  src={imgSrc}
                  alt={`Masala product: ${product.title}`}
                  className="max-w-full max-h-full object-contain"
                  onError={() => setImgSrc("/placeholder.svg")}
                  loading="lazy"
                  width={320}
                  height={224}
                />
                {product.badge && (
                  <span className="absolute top-3 left-3 bg-[#6FBF73] text-[#F9F7F1] text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-widest">
                    {product.badge}
                  </span>
                )}
                {isArchived && (
                  <span className="absolute top-3 right-3 bg-gray-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-widest z-20">Out of Stock</span>
                )}
              </div>
              
              {/* Content Section */}
              <div className="flex flex-col bg-[#F9F7F1] md:border-l border-[#FF6B35]/20">
                {/* Product Info */}
                <div className="p-4 md:p-6 border-b border-[#FF6B35]/20">
                <h2 className="text-xl xs:text-2xl sm:text-2xl md:text-3xl font-playfair font-bold text-[#2F2F2F] mb-3 sm:mb-4 leading-tight">
                    {product.title}
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  <div className="text-xs xs:text-sm text-green-600 font-medium flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 xs:h-5 xs:w-5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Delivery within 7 working days
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-xl xs:text-2xl sm:text-2xl font-bold text-[#FF6B35]">
                      ₹{getCurrentPrice().toFixed(2)}
                    </div>
                    {product.gst > 0 && (
                      <div className="text-xs xs:text-sm text-gray-600">
                        + {product.gst}% GST: ₹{(getCurrentPrice() * (product.gst / 100)).toFixed(2)}
                      </div>
                    )}
                    {product.shippingFee > 0 && (
                      <div className="text-xs xs:text-sm text-gray-600">
                        + Shipping Fee: ₹{product.shippingFee.toFixed(2)}
                      </div>
                    )}
                    <div className="text-sm xs:text-base font-semibold text-gray-800 pt-1">
                      Total: ₹{getCurrentTotal().toFixed(2)}
                    </div>
                  </div>
                  {/* Weight Selection - Reference Design Style */}
                  {product.gramOptions && product.gramOptions.length > 0 && (
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs xs:text-sm font-medium text-[#2F2F2F]">Weight:</span>
                        <span className="text-xs xs:text-sm font-medium text-[#2F2F2F]">
                          {selectedGramOption ? `${selectedGramOption.weight}g` : `${product.gramOptions[0]?.weight}g`}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {product.gramOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => setSelectedGramOption(option)}
                            className={`px-3 xs:px-4 py-2 rounded-md text-xs xs:text-sm font-medium transition-all duration-200 border touch-manipulation ${
                              selectedGramOption?.id === option.id 
                                ? 'bg-[#4CAF50] text-white border-[#4CAF50] shadow-md' 
                                : 'bg-white text-gray-700 border-gray-300 hover:border-[#4CAF50] hover:text-[#4CAF50]'
                            }`}
                          >
                            {option.weight}g
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={descriptionRef} className="text-sm text-gray-600">
                    <p className={`${!isDescriptionExpanded ? 'line-clamp-2' : ''} transition-all duration-500 ease-in-out leading-relaxed`}>
                      {product.description}
                    </p>
                    {product.description.length > 150 && (
                      <button
                        onClick={handleToggleDescription}
                        className="text-[#FF6B35] hover:text-[#D7263D] font-medium text-sm mt-1 transition-colors underline touch-manipulation"
                      >
                        {isDescriptionExpanded ? 'View Less' : 'View More'}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col xs:flex-row items-center justify-between gap-3 xs:gap-4">
                    <div className="flex items-center gap-3 xs:gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isArchived) setQuantity(Math.max(1, quantity - 1));
                        }}
                        className={`w-10 h-10 xs:w-8 xs:h-8 flex items-center justify-center rounded-full font-bold transition-colors touch-manipulation ${isArchived ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#D7263D] text-[#F9F7F1] hover:bg-[#FF6B35]'}`}
                        disabled={isArchived}
                      >
                        -
                      </button>
                      <span className="w-12 xs:w-10 text-center font-bold text-[#2F2F2F] text-lg xs:text-base">{quantity}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isArchived) setQuantity(quantity + 1);
                        }}
                        className={`w-10 h-10 xs:w-8 xs:h-8 flex items-center justify-center rounded-full font-bold transition-colors touch-manipulation ${isArchived ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#D7263D] text-[#F9F7F1] hover:bg-[#FF6B35]'}`}
                        disabled={isArchived}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={isArchived}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isArchived) handleAddToCart();
                      }}
                      className={`w-full xs:w-auto px-6 py-3 xs:py-2 font-bold rounded-xl transition-all duration-300 shadow-lg text-base xs:text-sm md:text-base touch-manipulation text-center ${isArchived ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#4CAF50] hover:bg-[#45a049] text-white'}`}
                      tabIndex={isArchived ? -1 : 0}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Reviews Section */}
              <div className="p-4 md:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-[#2F2F2F]">Reviews</h3>
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="text-[#D7263D] hover:text-[#FF6B35] font-medium transition-colors text-sm touch-manipulation"
                  >
                    Write a Review
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {showReviewForm ? (
                    <ReviewForm
                      onSubmit={handleSubmitReview}
                      onCancel={() => setShowReviewForm(false)}
                    />
                  ) : (
                    <ReviewList reviews={reviews.filter(r => r.productId === product.id)} />
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});
